<template>
  <div class="ads-hub">
    <!-- 页头 -->
    <div class="vtm-page-head">
      <div>
        <h1 class="vtm-page-head__title">{{ $t("advertsHub.title") }}</h1>
        <div class="vtm-page-head__sub">{{ $t("advertsHub.subtitle") }}</div>
      </div>
      <div v-if="tab === 'library'" class="ads-head-actions">
        <el-button type="primary" size="small" v-hasPerm="['adverts:insert']" icon="el-icon-plus" @click="addAdvert">{{
          $t("advertsHub.addCreative")
        }}</el-button>
      </div>
    </div>

    <!-- 主切换：素材库 | 投放 -->
    <div class="ads-seg-row">
      <el-radio-group v-model="tab" size="small">
        <el-radio-button label="library"><i class="el-icon-picture"></i> {{ $t("advertsHub.tabLibrary") }}</el-radio-button>
        <el-radio-button label="deploy"><i class="el-icon-position"></i> {{ $t("advertsHub.tabDeploy") }}</el-radio-button>
      </el-radio-group>
    </div>

    <!-- ============ 素材库 ============ -->
    <list-page
      v-if="tab === 'library'"
      :loading="libLoading"
      :error="libError"
      :empty="!advertsList.length"
      :empty-text="$t('advertsHub.emptyLibrary')"
      :total="libPage.totalSize || 0"
      :page-index="libPage.pageIndex"
      :page-size="libPage.pageSize"
      @page-change="loadLibrary"
      @size-change="libSizeChange"
      @retry="loadLibrary(libPage.pageIndex)"
    >
      <template #filters>
        <filter-bar :value="libSearch" :fields="libFilterFields" @search="onLibSearch" @reset="onLibReset"></filter-bar>
      </template>

      <div class="ads-grid">
        <div v-for="item in advertsList" :key="item.id" class="ad-tile">
          <div class="ad-tile__media">
            <el-image v-if="isImage(item)" :src="item.url" :preview-src-list="imageList" fit="cover" class="ad-tile__img"></el-image>
            <video v-else :src="item.url" class="ad-tile__img" controls></video>
            <span class="ad-tile__type">{{ mediaTypeLabel(item.type) }}</span>
          </div>
          <div class="ad-tile__body">
            <div class="ad-tile__name" :title="item.head">{{ item.head || $t("common.noTit") }}</div>
            <div class="ad-tile__meta">
              <span class="ad-tile__owner">{{ item.nickName || item.userName || "-" }}</span>
              <span class="ad-tile__time">{{ item.createTime }}</span>
            </div>
          </div>
          <div class="ad-tile__actions">
            <el-button size="mini" v-hasPerm="['adverts:update']" icon="el-icon-edit" @click="editAdvert(item)">{{ $t("common.edit") }}</el-button>
            <el-button size="mini" type="danger" v-hasPerm="['adverts:delete']" icon="el-icon-delete" @click="delAdvert(item)">{{
              $t("common.delete")
            }}</el-button>
          </div>
        </div>
      </div>
    </list-page>

    <!-- ============ 投放 ============ -->
    <list-page
      v-else
      :loading="depLoading"
      :error="depError"
      :empty="!depRows.length"
      :empty-text="$t('advertsHub.emptyDeploy')"
      :total="depPage.totalSize || 0"
      :page-index="depPage.pageIndex"
      :page-size="depPage.pageSize"
      @page-change="loadDeploy"
      @size-change="depSizeChange"
      @retry="loadDeploy(depPage.pageIndex)"
    >
      <vtm-table
        table-id="adverts-deploy"
        :columns="depColumns"
        :data="depRows"
        :loading="depLoading"
        :search-fields="deployMode === 'machine' ? ['equipmentNo', 'equipmentName'] : ['name', 'nickName']"
        :search-placeholder="deployMode === 'machine' ? $t('advertsHub.machineSearch') : $t('advertsHub.groupSearch')"
        :border="true"
      >
        <template #toolbar-left>
          <el-radio-group v-model="deployMode" size="small" @change="onDeployModeChange">
            <el-radio-button label="machine"><i class="el-icon-cpu"></i> {{ $t("advertsHub.deployMachines") }}</el-radio-button>
            <el-radio-button label="group"><i class="el-icon-collection"></i> {{ $t("advertsHub.deployGroups") }}</el-radio-button>
          </el-radio-group>
        </template>
        <template #equipmentNo="scope">
          <div class="ads-cell-machine">
            <span class="ads-cell-machine__no">{{ scope.row.equipmentNo }}</span>
            <span class="ads-cell-machine__name">{{ scope.row.equipmentName }}</span>
          </div>
        </template>
        <template #append>
          <el-table-column fixed="right" :label="$t('common.operate')" width="150" align="center">
            <template slot-scope="scope">
              <el-button size="mini" type="primary" plain icon="el-icon-video-camera" @click="openAssign(scope.row)">{{
                $t("advertsHub.manageAds")
              }}</el-button>
            </template>
          </el-table-column>
        </template>
      </vtm-table>
    </list-page>

    <!-- 新增/编辑素材 -->
    <add-adverts :adverts="adverts" :dialogAdverts="dialogAdverts" v-on:cancel="onAddCancel"></add-adverts>

    <!-- 投放分配抽屉 -->
    <advert-config-dialog
      :visible="assignVisible"
      :mode="assignMode"
      :equipment-no="assignEquipmentNo"
      :team-id="assignTeamId"
      :target-name="assignTargetName"
      @close="handleAssignClose"
    ></advert-config-dialog>
  </div>
</template>

<script>
  // 广告中心：素材库（创意管理）+ 投放（按设备/分组分配）。
  // 统一原「广告库 / 广告投放」两个入口；端点复用 Adverts.* / UserEquipmentAdvert.* / UserMachine(Group).page。
  import ListPage from "_c/common/ListPage.vue";
  import VtmTable from "_c/common/VtmTable.vue";
  import FilterBar from "_c/common/FilterBar.vue";
  import AddAdverts from "_c/adverts/AddAdverts";
  import AdvertConfigDialog from "_c/adverts/AdvertConfigDialog";

  const createDefaultAdvert = () => ({ id: "", type: 0, url: "", head: "", userName: "", nickName: "" });

  export default {
    name: "AdvertsHub",
    components: { ListPage, VtmTable, FilterBar, AddAdverts, AdvertConfigDialog },
    data() {
      return {
        tab: "library",
        // 素材库
        advertsList: [],
        imageList: [],
        libLoading: false,
        libError: false,
        libPage: { pageSize: 12, pageIndex: 1, totalSize: 0 },
        libSearch: { userName: "", head: "", nickName: "" },
        dialogAdverts: false,
        adverts: createDefaultAdvert(),
        // 投放
        deployMode: "machine",
        depRows: [],
        depLoading: false,
        depError: false,
        depPage: { pageSize: 10, pageIndex: 1, totalSize: 0 },
        depSearch: { name: "", equipmentName: "" },
        // 分配抽屉
        assignVisible: false,
        assignMode: "device",
        assignEquipmentNo: "",
        assignTeamId: "",
        assignTargetName: ""
      };
    },
    computed: {
      libFilterFields() {
        return [
          { key: "head", type: "text", placeholder: this.$t("searchAdverts.title"), width: "180px" },
          { key: "userName", type: "text", primary: false, placeholder: this.$t("searchAdverts.userName"), width: "180px" },
          { key: "nickName", type: "text", primary: false, placeholder: this.$t("searchAdverts.nickName"), width: "180px" }
        ];
      },
      depColumns() {
        if (this.deployMode === "machine") {
          return [
            { prop: "equipmentNo", label: this.$t("advertsHub.colMachine"), minWidth: 200, tooltip: false },
            { prop: "teamName", label: this.$t("advertsHub.colGroup"), width: 180, align: "center", formatter: this.groupName, tooltip: false }
          ];
        }
        return [
          { prop: "name", label: this.$t("advertsHub.colGroup"), minWidth: 200, tooltip: false },
          { prop: "nickName", label: this.$t("advertsHub.colOwner"), width: 180, align: "center", tooltip: false },
          { prop: "createTime", label: this.$t("advertsHub.colCreated"), width: 180, align: "center", tooltip: false }
        ];
      }
    },
    watch: {
      tab(val) {
        if (val === "deploy" && !this.depRows.length) {
          this.loadDeploy(1);
        }
      }
    },
    mounted() {
      this.loadLibrary();
    },
    methods: {
      isImage(ad) {
        return Number(ad && ad.type) === 0;
      },
      mediaTypeLabel(type) {
        return Number(type) === 1 ? this.$t("addAdverts.video") : this.$t("addAdverts.image");
      },
      groupName(row) {
        return (row.userEquipmentTeam && row.userEquipmentTeam.name) || row.teamName || "-";
      },
      // ---- 素材库 ----
      loadLibrary(pageIndex) {
        this.libLoading = true;
        this.libError = false;
        this.libPage.pageIndex = pageIndex ? pageIndex : 1;
        const obj = this.$cloud.objMerge(this.libSearch, this.libPage);
        this.$api
          .request(this.$url.Adverts.page, this.$method.get, obj)
          .then(res => {
            if (res.code === 0) {
              this.advertsList = res.data.records || [];
              this.libPage.totalSize = parseInt(res.data.total) || 0;
              this.imageList = this.advertsList.filter(a => this.isImage(a) && a.url).map(a => a.url);
            }
            this.libLoading = false;
          })
          .catch(() => {
            this.libLoading = false;
            this.libError = true;
          });
      },
      libSizeChange(pageSize) {
        this.libPage.pageSize = pageSize;
        this.loadLibrary();
      },
      onLibSearch(data) {
        this.libSearch = data;
        this.loadLibrary(1);
      },
      onLibReset(data) {
        this.libSearch = data;
        this.loadLibrary(1);
      },
      addAdvert() {
        this.adverts = createDefaultAdvert();
        this.dialogAdverts = true;
      },
      editAdvert(row) {
        this.adverts = createDefaultAdvert();
        Object.keys(this.adverts).forEach(key => {
          this.adverts[key] = row[key];
        });
        this.adverts.id = row.id;
        this.adverts.type = Number(row.type) === 1 ? 1 : 0;
        this.dialogAdverts = true;
      },
      onAddCancel(dialog, state) {
        this.dialogAdverts = dialog;
        if (state) {
          this.loadLibrary(this.libPage.pageIndex);
        }
      },
      delAdvert(row) {
        this.$confirm(this.$t("adverts.delete"), this.$t("common.warning"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        }).then(() => {
          this.$api.request(this.$url.Adverts.delete, this.$method.delete, { id: row.id }).then(res => {
            if (res.code === 0) {
              this.$notify({ title: this.$t("common.tip"), message: res.msg, type: "success" });
              this.loadLibrary(this.libPage.pageIndex);
            }
          });
        });
      },
      // ---- 投放 ----
      onDeployModeChange() {
        this.depRows = [];
        this.depPage.pageIndex = 1;
        this.loadDeploy(1);
      },
      loadDeploy(pageIndex) {
        this.depLoading = true;
        this.depError = false;
        this.depPage.pageIndex = pageIndex ? pageIndex : 1;
        const isMachine = this.deployMode === "machine";
        const url = isMachine ? this.$url.UserMachine.page : this.$url.UserMachineGroup.page;
        const params = {
          pageIndex: this.depPage.pageIndex,
          pageSize: this.depPage.pageSize
        };
        this.$api
          .request(url, this.$method.get, params)
          .then(res => {
            if (res.code === 0 && res.data) {
              this.depRows = res.data.records || [];
              this.depPage.totalSize = parseInt(res.data.total) || 0;
            }
            this.depLoading = false;
          })
          .catch(() => {
            this.depLoading = false;
            this.depError = true;
          });
      },
      depSizeChange(pageSize) {
        this.depPage.pageSize = pageSize;
        this.loadDeploy(1);
      },
      openAssign(row) {
        if (this.deployMode === "machine") {
          this.assignMode = "device";
          this.assignEquipmentNo = row.equipmentNo || "";
          this.assignTeamId = "";
          this.assignTargetName = row.equipmentName || row.equipmentNo || "";
        } else {
          this.assignMode = "group";
          this.assignEquipmentNo = "";
          this.assignTeamId = row.id || "";
          this.assignTargetName = row.name || "";
        }
        this.assignVisible = true;
      },
      handleAssignClose(dialog, refresh) {
        this.assignVisible = false;
        if (refresh) {
          this.loadDeploy(this.depPage.pageIndex);
        }
      }
    }
  };
</script>

<style scoped lang="scss">
  .ads-hub {
    padding-bottom: 24px;
  }
  .ads-head-actions {
    display: flex;
    gap: 8px;
  }
  .ads-seg-row {
    padding: 0 0 14px;
  }

  .ads-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 16px;
    padding: 4px;
  }
  .ad-tile {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    overflow: hidden;
    background: var(--vtm-surface);
    transition: box-shadow var(--vtm-speed) ease, transform var(--vtm-speed) ease;

    &:hover {
      box-shadow: var(--vtm-e2);
      transform: translateY(-2px);
    }
  }
  .ad-tile__media {
    position: relative;
    width: 100%;
    height: 150px;
    background: var(--vtm-well);
  }
  .ad-tile__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .ad-tile__type {
    position: absolute;
    left: 8px;
    top: 8px;
    padding: 2px 8px;
    border-radius: 10px;
    background: rgba(16, 42, 67, 0.72);
    color: #fff;
    font-size: 10.5px;
    font-weight: 600;
  }
  .ad-tile__body {
    padding: 10px 12px;
    flex: 1;
  }
  .ad-tile__name {
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-heading);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ad-tile__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 4px;
    font-size: 11px;
    color: var(--vtm-ink-3);
  }
  .ad-tile__owner {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ad-tile__time {
    flex-shrink: 0;
  }
  .ad-tile__actions {
    display: flex;
    gap: 8px;
    padding: 0 12px 12px;

    ::v-deep .el-button {
      flex: 1;
      margin: 0;
    }
  }

  .ads-cell-machine {
    display: flex;
    flex-direction: column;
    line-height: 1.3;
  }
  .ads-cell-machine__no {
    font-weight: 600;
    color: var(--vtm-ink);
  }
  .ads-cell-machine__name {
    font-size: 12px;
    color: var(--vtm-ink-3);
  }
</style>
